import { getCountryByCode, validatePostalCode, validatePostalCodes } from "postal-code-checker";

import { CreateAddress } from "~/gql/graphqlBase.generated";

const SUBDIVISION_CODE_COUNTRIES = new Set(["US", "CA"]);

// US and CA spellings must stay aligned with the server-side ValidCountry.Sanitize.
const FULL_NAME_COUNTRY_ALIASES: Record<string, string> = {
	CAN: "CA",
	CANADA: "CA",
	MEXICO: "MX",
	"UNITED STATES": "US",
	"UNITED STATES OF AMERICA": "US",
	USA: "US"
};

export const resolveCountryCode = (country?: string): string => {
	const upperTrimmed = country?.trim().toUpperCase() ?? "";
	const aliased = FULL_NAME_COUNTRY_ALIASES[upperTrimmed] ?? upperTrimmed;
	return getCountryByCode(aliased)?.countryCode ?? aliased;
};

export const usesSubdivisionCodes = (country?: string): boolean =>
	SUBDIVISION_CODE_COUNTRIES.has(resolveCountryCode(country));

const isLenientPostalCode = (value: string): boolean => /^[a-zA-Z0-9\s-]+$/.test(value);

const ZIP_CODE_MESSAGE_KEYS: Partial<Record<string, string>> = {
	CA: "common:addressValidation.zipCode.invalidCa",
	MX: "common:addressValidation.zipCode.invalidMx",
	US: "common:addressValidation.zipCode.invalidUs"
};

/**
 * State/Province abbreviation validation
 */
export const US_STATE_CODES = [
	"AL",
	"AK",
	"AZ",
	"AR",
	"CA",
	"CO",
	"CT",
	"DE",
	"FL",
	"GA",
	"HI",
	"ID",
	"IL",
	"IN",
	"IA",
	"KS",
	"KY",
	"LA",
	"ME",
	"MD",
	"MA",
	"MI",
	"MN",
	"MS",
	"MO",
	"MT",
	"NE",
	"NV",
	"NH",
	"NJ",
	"NM",
	"NY",
	"NC",
	"ND",
	"OH",
	"OK",
	"OR",
	"PA",
	"RI",
	"SC",
	"SD",
	"TN",
	"TX",
	"UT",
	"VT",
	"VA",
	"WA",
	"WV",
	"WI",
	"WY"
];

export const CANADIAN_PROVINCE_CODES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

export interface StateProvince {
	code: string;
	name: string;
}

export const STATE_PROVINCES: StateProvince[] = [
	{ code: "AL", name: "Alabama" },
	{ code: "AK", name: "Alaska" },
	{ code: "AZ", name: "Arizona" },
	{ code: "AR", name: "Arkansas" },
	{ code: "CA", name: "California" },
	{ code: "CO", name: "Colorado" },
	{ code: "CT", name: "Connecticut" },
	{ code: "DE", name: "Delaware" },
	{ code: "FL", name: "Florida" },
	{ code: "GA", name: "Georgia" },
	{ code: "HI", name: "Hawaii" },
	{ code: "ID", name: "Idaho" },
	{ code: "IL", name: "Illinois" },
	{ code: "IN", name: "Indiana" },
	{ code: "IA", name: "Iowa" },
	{ code: "KS", name: "Kansas" },
	{ code: "KY", name: "Kentucky" },
	{ code: "LA", name: "Louisiana" },
	{ code: "ME", name: "Maine" },
	{ code: "MD", name: "Maryland" },
	{ code: "MA", name: "Massachusetts" },
	{ code: "MI", name: "Michigan" },
	{ code: "MN", name: "Minnesota" },
	{ code: "MS", name: "Mississippi" },
	{ code: "MO", name: "Missouri" },
	{ code: "MT", name: "Montana" },
	{ code: "NE", name: "Nebraska" },
	{ code: "NV", name: "Nevada" },
	{ code: "NH", name: "New Hampshire" },
	{ code: "NJ", name: "New Jersey" },
	{ code: "NM", name: "New Mexico" },
	{ code: "NY", name: "New York" },
	{ code: "NC", name: "North Carolina" },
	{ code: "ND", name: "North Dakota" },
	{ code: "OH", name: "Ohio" },
	{ code: "OK", name: "Oklahoma" },
	{ code: "OR", name: "Oregon" },
	{ code: "PA", name: "Pennsylvania" },
	{ code: "RI", name: "Rhode Island" },
	{ code: "SC", name: "South Carolina" },
	{ code: "SD", name: "South Dakota" },
	{ code: "TN", name: "Tennessee" },
	{ code: "TX", name: "Texas" },
	{ code: "UT", name: "Utah" },
	{ code: "VT", name: "Vermont" },
	{ code: "VA", name: "Virginia" },
	{ code: "WA", name: "Washington" },
	{ code: "WV", name: "West Virginia" },
	{ code: "WI", name: "Wisconsin" },
	{ code: "WY", name: "Wyoming" },
	{ code: "AB", name: "Alberta" },
	{ code: "BC", name: "British Columbia" },
	{ code: "MB", name: "Manitoba" },
	{ code: "NB", name: "New Brunswick" },
	{ code: "NL", name: "Newfoundland and Labrador" },
	{ code: "NS", name: "Nova Scotia" },
	{ code: "ON", name: "Ontario" },
	{ code: "PE", name: "Prince Edward Island" },
	{ code: "QC", name: "Quebec" },
	{ code: "SK", name: "Saskatchewan" },
	{ code: "NT", name: "Northwest Territories" },
	{ code: "NU", name: "Nunavut" },
	{ code: "YT", name: "Yukon" }
];

export function resolveStateProvinceCode(value?: string | null, country?: string): string | null {
	const countryCode = resolveCountryCode(country);
	if (!SUBDIVISION_CODE_COUNTRIES.has(countryCode)) return null;

	const upperValue = value?.trim().toUpperCase() ?? "";
	if (!upperValue) return null;

	const validCodes = countryCode === "US" ? US_STATE_CODES : CANADIAN_PROVINCE_CODES;
	if (validCodes.includes(upperValue)) return upperValue;

	const byName = STATE_PROVINCES.find((sp) => sp.name.toUpperCase() === upperValue && validCodes.includes(sp.code));
	return byName?.code ?? null;
}

export const findStateProvinceByCode = (code?: string | null): StateProvince | undefined =>
	STATE_PROVINCES.find((sp) => sp.code === code);

export const findStateProvince = (value: string, country?: string): StateProvince | undefined =>
	findStateProvinceByCode(value.trim().toUpperCase()) ??
	findStateProvinceByCode(resolveStateProvinceCode(value, country));

// Must stay aligned with the server-side ValidStateProvince code length for US and CA.
const STATE_PROVINCE_CODE_LENGTH = 2;

export const exceedsStateProvinceCodeLength = (value?: string | null, country?: string): boolean =>
	usesSubdivisionCodes(country) && (value?.trim().length ?? 0) > STATE_PROVINCE_CODE_LENGTH;

/**
 * Address field length limits
 */
export const ADDRESS_FIELD_LIMITS = {
	ADDRESS_LINE_1: 100,
	ADDRESS_LINE_2: 100,
	CITY: 50,
	COUNTRY: 3,
	STATE_PROVINCE: 50,
	TO_NAME: 100,
	ZIP_CODE: 20
};

export interface AddressValidationMessage {
	params?: { max: number };
	translationKey: string;
}

export interface AddressValidationResult {
	errors: Record<string, AddressValidationMessage>;
	isValid: boolean;
}

/**
 * Individual field validation functions
 */
export function validateToName(value: string): AddressValidationMessage | null {
	if (!value?.trim()) {
		return { translationKey: "common:addressValidation.toName.required" };
	}
	if (value.length > ADDRESS_FIELD_LIMITS.TO_NAME) {
		return { params: { max: ADDRESS_FIELD_LIMITS.TO_NAME }, translationKey: "common:addressValidation.toName.tooLong" };
	}
	// Check for potentially invalid characters
	if (!/^[a-zA-Z0-9\s\-.,&']+$/.test(value)) {
		return { translationKey: "common:addressValidation.toName.invalidCharacters" };
	}
	return null;
}

export function validateAddressLine1(value: string): AddressValidationMessage | null {
	if (!value?.trim()) {
		return { translationKey: "common:addressValidation.addressLine1.required" };
	}
	if (value.length > ADDRESS_FIELD_LIMITS.ADDRESS_LINE_1) {
		return {
			params: { max: ADDRESS_FIELD_LIMITS.ADDRESS_LINE_1 },
			translationKey: "common:addressValidation.addressLine1.tooLong"
		};
	}
	// Basic address format validation
	if (!/^[a-zA-Z0-9\s\-.,#/]+$/.test(value)) {
		return { translationKey: "common:addressValidation.addressLine1.invalidCharacters" };
	}
	return null;
}

export function validateAddressLine2(value?: string): AddressValidationMessage | null {
	if (!value?.trim()) {
		return null; // Optional field
	}
	if (value.length > ADDRESS_FIELD_LIMITS.ADDRESS_LINE_2) {
		return {
			params: { max: ADDRESS_FIELD_LIMITS.ADDRESS_LINE_2 },
			translationKey: "common:addressValidation.addressLine2.tooLong"
		};
	}
	if (!/^[a-zA-Z0-9\s\-.,#/]+$/.test(value)) {
		return { translationKey: "common:addressValidation.addressLine2.invalidCharacters" };
	}
	return null;
}

export function validateCity(value: string): AddressValidationMessage | null {
	if (!value?.trim()) {
		return { translationKey: "common:addressValidation.city.required" };
	}
	if (value.length > ADDRESS_FIELD_LIMITS.CITY) {
		return { params: { max: ADDRESS_FIELD_LIMITS.CITY }, translationKey: "common:addressValidation.city.tooLong" };
	}
	// Cities should generally only contain letters, spaces, hyphens, apostrophes
	if (!/^[a-zA-Z\s\-.]+$/.test(value)) {
		return { translationKey: "common:addressValidation.city.invalidCharacters" };
	}
	return null;
}

export function validateStateProvince(value: string, country: string = "US"): AddressValidationMessage | null {
	const trimmed = value?.trim() ?? "";
	const countryCode = resolveCountryCode(country);

	if (!SUBDIVISION_CODE_COUNTRIES.has(countryCode)) {
		if (!trimmed) {
			return null;
		}
		if (trimmed.length > ADDRESS_FIELD_LIMITS.STATE_PROVINCE) {
			return {
				params: { max: ADDRESS_FIELD_LIMITS.STATE_PROVINCE },
				translationKey: "common:addressValidation.stateProvince.tooLong"
			};
		}
		return null;
	}

	if (!trimmed) {
		return { translationKey: "common:addressValidation.stateProvince.required" };
	}

	const upperValue = trimmed.toUpperCase();

	if (countryCode === "US") {
		if (!US_STATE_CODES.includes(upperValue)) {
			return { translationKey: "common:addressValidation.stateProvince.invalidUs" };
		}
	} else if (!CANADIAN_PROVINCE_CODES.includes(upperValue)) {
		return { translationKey: "common:addressValidation.stateProvince.invalidCa" };
	}

	return null;
}

export function validateZipCode(value: string, country: string = "US"): AddressValidationMessage | null {
	const trimmed = value?.trim() ?? "";

	if (!trimmed) {
		return { translationKey: "common:addressValidation.zipCode.required" };
	}

	if (trimmed.length > ADDRESS_FIELD_LIMITS.ZIP_CODE) {
		return {
			params: { max: ADDRESS_FIELD_LIMITS.ZIP_CODE },
			translationKey: "common:addressValidation.zipCode.tooLong"
		};
	}

	const countryCode = resolveCountryCode(country);
	const countryInfo = getCountryByCode(countryCode);

	if (!countryInfo || countryInfo.postalCodePatterns.length === 0) {
		return isLenientPostalCode(trimmed)
			? null
			: { translationKey: "common:addressValidation.zipCode.invalidCharacters" };
	}

	if (!validatePostalCode(countryCode, trimmed)) {
		return {
			translationKey: ZIP_CODE_MESSAGE_KEYS[countryCode] ?? "common:addressValidation.zipCode.invalidForCountry"
		};
	}

	return null;
}

export function validatePostalCodesForCountry(country: string, values: string[]): boolean[] {
	const countryCode = resolveCountryCode(country);
	const countryInfo = getCountryByCode(countryCode);

	if (!countryInfo || countryInfo.postalCodePatterns.length === 0) {
		return values.map((value) => isLenientPostalCode(value.trim()));
	}

	return validatePostalCodes(countryCode, values);
}

export interface PostalCodeBatchEntry {
	country: string;
	value: string;
}

export function validatePostalCodesBatch(entries: PostalCodeBatchEntry[]): boolean[] {
	const results = new Array<boolean>(entries.length);
	const indexesByCountry = new Map<string, number[]>();

	entries.forEach((entry, index) => {
		const countryCode = resolveCountryCode(entry.country);
		indexesByCountry.set(countryCode, [...(indexesByCountry.get(countryCode) ?? []), index]);
	});

	indexesByCountry.forEach((indexes, countryCode) => {
		const validated = validatePostalCodesForCountry(
			countryCode,
			indexes.map((index) => entries[index].value)
		);
		indexes.forEach((index, position) => {
			results[index] = validated[position];
		});
	});

	return results;
}

export function validateCountry(value?: string): AddressValidationMessage | null {
	if (!value?.trim()) {
		return null; // Optional field, defaults to US
	}

	if (value.length > ADDRESS_FIELD_LIMITS.COUNTRY) {
		return {
			params: { max: ADDRESS_FIELD_LIMITS.COUNTRY },
			translationKey: "common:addressValidation.country.tooLong"
		};
	}

	// Basic country code validation (could be enhanced with full ISO list)
	const upperValue = value.toUpperCase();

	if (value.length <= 3 && !/^[A-Z]+$/.test(upperValue)) {
		return { translationKey: "common:addressValidation.country.lettersOnly" };
	}

	return null;
}

const SERVER_ADDRESS_LIMITS = {
	ADDRESS_LINE_1: 64,
	CITY: 128,
	TO_NAME: 128
};

export function isPostableAddress(
	address: Pick<CreateAddress, "addressLine1" | "city" | "country" | "stateProvince" | "toName" | "zipcode">
): boolean {
	const toName = address.toName?.trim() ?? "";
	const addressLine1 = address.addressLine1?.trim() ?? "";
	const city = address.city?.trim() ?? "";

	if (!toName || toName.length > SERVER_ADDRESS_LIMITS.TO_NAME) return false;
	if (!addressLine1 || addressLine1.length > SERVER_ADDRESS_LIMITS.ADDRESS_LINE_1) return false;
	if (!city || city.length > SERVER_ADDRESS_LIMITS.CITY) return false;

	const countryCode = resolveCountryCode(address.country);
	if (!getCountryByCode(countryCode)) return false;
	if (!usesSubdivisionCodes(countryCode)) return true;

	const stateProvince = address.stateProvince?.trim().toUpperCase() ?? "";
	const validStateCodes = countryCode === "US" ? US_STATE_CODES : CANADIAN_PROVINCE_CODES;
	if (!validStateCodes.includes(stateProvince)) return false;

	const zipcode = address.zipcode?.trim() ?? "";
	return zipcode.length > 0 && validatePostalCode(countryCode, zipcode);
}

/**
 * Comprehensive address validation
 */
export function validateCompleteAddress(address: CreateAddress): AddressValidationResult {
	const errors: Record<string, AddressValidationMessage> = {};

	// Check if any address field has data to determine if validation is needed
	const hasAnyAddressField = Boolean(
		address.toName || address.addressLine1 || address.city || address.stateProvince || address.zipcode
	);

	if (!hasAnyAddressField) {
		// No address data provided, no validation needed
		return { errors, isValid: true };
	}

	// Validate each field
	const toNameError = validateToName(address.toName || "");
	if (toNameError) errors.toName = toNameError;

	const addressLine1Error = validateAddressLine1(address.addressLine1 || "");
	if (addressLine1Error) errors.addressLine1 = addressLine1Error;

	const addressLine2Error = validateAddressLine2(address.addressLine2);
	if (addressLine2Error) errors.addressLine2 = addressLine2Error;

	const cityError = validateCity(address.city || "");
	if (cityError) errors.city = cityError;

	const stateProvinceError = validateStateProvince(address.stateProvince || "", address.country);
	if (stateProvinceError) errors.stateProvince = stateProvinceError;

	const zipCodeError = validateZipCode(address.zipcode || "", address.country);
	if (zipCodeError) errors.zipcode = zipCodeError;

	const countryError = validateCountry(address.country);
	if (countryError) errors.country = countryError;

	return {
		errors,
		isValid: Object.keys(errors).length === 0
	};
}

/**
 * Format an address object into a single comma-separated line.
 * Used wherever a compact one-line address display is needed.
 */
export const formatAddress = (addr: {
	addressLine1?: string | null;
	addressLine2?: string | null;
	city?: string | null;
	stateProvince?: string | null;
	zipcode?: string | null;
}): string => {
	const parts = [
		addr.addressLine1,
		addr.addressLine2,
		[addr.city, addr.stateProvince].filter(Boolean).join(", "),
		addr.zipcode
	].filter(Boolean);
	return parts.join(", ");
};

/**
 * Format address for display
 */
export function formatAddressForDisplay(address: CreateAddress): string {
	const lines: string[] = [];

	if (address.toName) {
		lines.push(address.toName);
	}

	if (address.addressLine1) {
		lines.push(address.addressLine1);
	}

	if (address.addressLine2) {
		lines.push(address.addressLine2);
	}

	const cityStateZip = [address.city, address.stateProvince && `${address.stateProvince}`, address.zipcode]
		.filter(Boolean)
		.join(" ");

	if (cityStateZip) {
		lines.push(cityStateZip);
	}

	return lines.join("\n");
}

/**
 * Normalize address data for consistency
 */
export function normalizeAddress(address: CreateAddress): CreateAddress {
	const normalizedCountry = address.country?.trim().toUpperCase() || "US";
	const trimmedStateProvince = address.stateProvince?.trim();

	return {
		...address,
		addressLine1: address.addressLine1?.trim(),
		addressLine2: address.addressLine2?.trim(),
		city: address.city?.trim(),
		country: normalizedCountry,
		stateProvince: usesSubdivisionCodes(normalizedCountry) ? trimmedStateProvince?.toUpperCase() : trimmedStateProvince,
		toName: address.toName?.trim(),
		zipcode: address.zipcode?.trim().toUpperCase()
	};
}
